import { FC } from 'react';
import { Link, useLocation, useHistory } from 'react-router-dom';
import classNames from 'classnames';
import { PaginatorWithResult } from 'jbc-front/components/SearchForm';
import { AsyncTaskError } from '@/components/AsyncTaskError';
import { LoadingPage } from '@/components/LoadingPage';
import { Table } from '@/components/Table';
import { Content } from '@/components/layout/Content';
import { useDisplayEmployeeLimit } from '@/components/utils/display_employee_limit';
import { dateFormat, displayFormat, fullNameFormat } from '@/components/utils/formatter';
import { useQuery } from '@/hooks/graphql/useQuery';
import { SearchForm } from './parts/SearchForm';
import { EMPLOYEES, Employees, EmployeesQueryVariables } from './query';
import {
  convertFormToSearchParams,
  convertSearchParamsToForm,
  convertSearchParamsToGraphQL,
  convertSearchParamsToQueryString,
  getSearchParamsFromLocation,
  hasConditions
} from './search';
import styles from './SearchPersonalResults.scss';

export const SearchPersonalResults: FC = () => {
  const location = useLocation();
  const history = useHistory();
  const [limit] = useDisplayEmployeeLimit();
  const searchParams = { ...getSearchParamsFromLocation(location), limit };

  const { data, loading } = useQuery<Employees, EmployeesQueryVariables>(EMPLOYEES, {
    variables: convertSearchParamsToGraphQL(searchParams),
    fetchPolicy: 'no-cache'
  });

  return (
    <div>
      <div className="l-main-title-wrap">
        <h1 className="m-title-main">個人結果一覧</h1>
      </div>
      <AsyncTaskError displayTypes={['regular_health_checkup_result_csv_import']} />
      <Content size="xl-4">
        <SearchForm
          values={convertSearchParamsToForm(searchParams)}
          totalCount={data?.client?.employees?.totalCount}
          isLoading={loading}
          onSubmit={values => {
            const { _page, ...newSearchParams } = convertFormToSearchParams(values); // reset page
            history.push({ search: convertSearchParamsToQueryString(newSearchParams) });
          }}
          onClear={() => history.push({ search: '' })}
          hasConditions={hasConditions}
        />
        {loading ? (
          <LoadingPage />
        ) : !data?.client ? (
          <div className="l-wrap-xs u-pt100">
            <div className="l-box-message">
              <p className="m-title-sub u-pt0 u-ta-c">従業員を取得できませんでした。</p>
            </div>
          </div>
        ) : (
          <Table fixed className={styles.table}>
            <thead>
              <tr>
                <th className={styles.staffCodeColumn}>スタッフコード</th>
                <th className={styles.nameColumn}>氏名</th>
                <th className={styles.employmentTypeColumn}>雇用形態</th>
                <th className={styles.groupColumn}>グループ</th>
                <th className={styles.positionColumn}>役職</th>
                <th className={styles.joinedAtColumn}>入社日</th>
                <th className={styles.retiredAtColumn}>退職日</th>
              </tr>
            </thead>
            <tbody>
              {data.client.employees.list.map(employee => (
                <tr key={employee.personalId} className={classNames(styles.tr, 'table-hover')}>
                  <td className={styles.staffCodeColumn}>
                    <Link
                      className={styles.stretchedLink}
                      to={`/regular_health_checkup/personal_results/${employee.personalId}`}>
                      {displayFormat(employee.staffCode)}
                    </Link>
                  </td>
                  <td className={styles.nameColumn}>{fullNameFormat(employee.lastName, employee.firstName)}</td>
                  <td className={styles.employmentTypeColumn}>{displayFormat(employee.employmentType)}</td>
                  <td className={styles.groupColumn}>
                    {employee.groups && employee.groups.length > 0
                      ? employee.groups.map(group => (
                          <span key={group}>
                            {group}
                            <br />
                          </span>
                        ))
                      : displayFormat()}
                  </td>
                  <td className={styles.positionColumn}>{displayFormat(employee.position)}</td>
                  <td className={styles.joinedAtColumn}>{dateFormat(employee.joinedAt, 'YYYY/MM/DD')}</td>
                  <td className={styles.retiredAtColumn}>{dateFormat(employee.retiredAt, 'YYYY/MM/DD')}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
        <PaginatorWithResult
          onClick={(page: number) =>
            history.push({ search: convertSearchParamsToQueryString({ ...searchParams, _page: String(page) }) })
          }
          limit={limit}
          count={data?.client?.employees?.totalCount}
          page={searchParams._page}
          isLoading={loading}
          failureMessage="従業員が見つかりませんでした"
        />
      </Content>
    </div>
  );
};
